import React, { ReactNode, useId } from 'react';
import { Flex } from 'glints-aries/es';
import { Icon, Spinner, Tooltip, Typography } from 'glints-aries/es/@next';
import { Neutral } from 'glints-aries/es/@next/utilities/colors';
import {
  space4,
  space8,
  space12,
  space16,
} from 'glints-aries/es/@next/utilities/spacing';
import { compact } from 'lodash-es';
import { useController, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import useUploadFile from '../../../../../common/hooks/requests/useUploadFile';
import {
  useCompanyVerificationFormContext,
  useIsViewOnlyMode,
} from '../../FormContext';
import { useCompanyVerificationTracking } from '../../hooks/useCompanyVerificationTracking';
import { getAssetFileNameFromURL, isURL } from '../../utils';
import { FormContentStyles } from '../styles.sc';
import MultiFileUploaderList from './MultiFileUploaderList';
import { MultiFileUploaderProps } from './types';

const DEFAULT_MAX_FILE_SIZE = 10;
function MultiFileUploader({
  title,
  assetType,
  required,
  fieldName,
  maxImages = 1,
  hideTitle,
  maxFileSizeInMB = DEFAULT_MAX_FILE_SIZE,
  tooltip,
}: MultiFileUploaderProps) {
  const isViewOnly = useIsViewOnlyMode();
  const { control } = useCompanyVerificationFormContext();
  const fieldValue = useWatch({
    control,
    name: fieldName,
  }) as string[];

  return (
    <Flex flexDirection="column" gap={space12} alignItems="flex-start">
      <If condition={!hideTitle}>
        <Flex gap={space4}>
          <Typography variant="body2">
            {title}
            {required && <FormContentStyles.Asterisk />}
          </Typography>
          {tooltip}
        </Flex>
      </If>

      <Flex flexDirection="column" gap={space8}>
        <Flex gap={space16}>
          {Array(isViewOnly ? compact(fieldValue).length : maxImages)
            .fill({})
            .map((_, index) => (
              <RenderButton
                key={index}
                assetType={assetType}
                fieldName={fieldName}
                index={index}
                maxFileSizeInMB={maxFileSizeInMB}
              />
            ))}
        </Flex>

        <If condition={!isViewOnly}>
          <Typography variant="subtitle2" color={Neutral.B40}>
            <FormattedMessage
              id="text-acceptable-formats-image-max"
              defaultMessage="<bold>Acceptable formats:</bold> jpg, jpeg, png | <bold>Maximum size: {maxSize}MB</bold>"
              tagName="span"
              values={{
                bold: (...chunks: ReactNode[]) => (
                  <Typography variant="caption" as="span" color={Neutral.B40}>
                    {chunks}
                  </Typography>
                ),
                maxSize: maxFileSizeInMB,
              }}
            />
          </Typography>
        </If>
      </Flex>
    </Flex>
  );
}

function RenderButton({
  fieldName,
  assetType,
  index,
  maxFileSizeInMB = DEFAULT_MAX_FILE_SIZE,
}: Pick<
  MultiFileUploaderProps,
  'fieldName' | 'assetType' | 'maxFileSizeInMB'
> & {
  index: number;
}) {
  const {
    field: { onChange, value: fieldValue },
  } = useController({
    name: `${fieldName}.${index}`,
  });
  const inputElemId = useId();
  const { control } = useCompanyVerificationFormContext();
  const allFiles = useWatch({
    control,
    name: fieldName,
  }) as string[];
  const originPhotoCount = compact(allFiles).length;
  const { trackCompanyVerificationMultiFileUploadEvent } =
    useCompanyVerificationTracking();

  const {
    uploadFile,
    file,
    originalFile,
    loading,
    reset: resetFile,
    error,
  } = useUploadFile({
    assetType,
    maxSizeInMB: maxFileSizeInMB,
    allowedMimeTypes: ['image/jpeg', 'image/png'],
    onSuccess: uploadedFilePath => {
      onChange(uploadedFilePath);
      trackCompanyVerificationMultiFileUploadEvent(
        fieldName,
        originPhotoCount + 1
      );
    },
  });
  const isViewOnly = useIsViewOnlyMode();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await uploadFile(file);
    }
  };

  const handleRemoveFile = () => {
    resetFile();
    onChange('');
  };

  const selectedFile = fieldValue
    ? isURL(fieldValue as string)
      ? {
          assetURL: fieldValue as string,
          assetFileName: getAssetFileNameFromURL(fieldValue as string),
        }
      : {
          assetURL:
            (file && originalFile && URL.createObjectURL(originalFile)) || '',
          assetFileName: file?.name,
        }
    : null;

  const handlePreviewImage = () => {
    if (selectedFile) {
      window.open(selectedFile.assetURL, '_blank');
    }
  };

  const UploadButtonContent = (
    <label htmlFor={inputElemId}>
      <FormContentStyles.ImageUploadButton
        onClick={e => e.currentTarget.parentElement?.click()}
        data-error={Boolean(error)}
      >
        <Choose>
          <When condition={loading}>
            <Spinner fill={Neutral.B40} width={24} height={24} />
          </When>
          <Otherwise>
            <Icon name="ri-add" fill={Neutral.B40} width={24} height={24} />
            <Typography variant="caption">
              <FormattedMessage
                id="interactive-upload"
                defaultMessage="Upload"
              />
            </Typography>
          </Otherwise>
        </Choose>
      </FormContentStyles.ImageUploadButton>
    </label>
  );

  return (
    <Choose>
      <When condition={selectedFile}>
        <FormContentStyles.ImageUploadButton
          data-ignore-padding={true}
          data-image-selected={true}
        >
          <img src={selectedFile?.assetURL} />
          <div
            className="remove-file-icon"
            onClick={isViewOnly ? handlePreviewImage : handleRemoveFile}
          >
            <Icon
              name={isViewOnly ? 'ri-eye-fill' : 'ri-delete-bin-fill'}
              fill={isViewOnly ? Neutral.B85 : Neutral.B40}
              width={16}
              height={16}
            />
          </div>
        </FormContentStyles.ImageUploadButton>
      </When>
      <Otherwise>
        <input
          type="file"
          id={inputElemId}
          accept="image/jpeg, image/png"
          hidden={true}
          onChange={handleFileChange}
        />
        <Choose>
          <When condition={error}>
            <Tooltip content={error} zIndex={1004}>
              {UploadButtonContent}
            </Tooltip>
          </When>
          <Otherwise>{UploadButtonContent}</Otherwise>
        </Choose>
      </Otherwise>
    </Choose>
  );
}
MultiFileUploader.List = MultiFileUploaderList;
MultiFileUploader.Default = MultiFileUploader;

export default MultiFileUploader;
